import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';

const GET_USER = gql`
  query getUser($firebaseId: String!) {
    user(firebaseId: $firebaseId) {
      ... on User {
        id
        firebaseId
        firstName
        lastName
        email
        phone
        company
        profilePicUrl
        userType
        municipalityId
        titleCompanyId
        titleCompany
        status
        municipalityName
        emailVerification {
          completed
        }
        roleData {
          _id
          key
          name
          description
          status
        }
        userTypeData {
          _id
          name
          key
          status
        }
        permissions {
          _id
          name
          type
          key
          selected
          selectedValue
          option_type
          options
          children
        }
        processAccount {
          status
          tempPassword
        }
        source
      }
      ... on UserError {
        message
        success
      }
    }
  }
`;
const GET_USERS = gql`
  query getUsers($search: String, $page: Int!, $length: Int!) {
    users(search: $search, page: $page, length: $length) {
      success
      message
      totalRecords
      totalPages
      currentPage
      user {
        id
        firstName
        lastName
        email
        company
        phone
        profilePicUrl
        userType
        titleCompanyId
        titleCompany
        emailVerification {
          completed
          code
        }
        roleData {
          _id
          key
          name
          description
          status
        }
        userTypeData {
          _id
          name
          key
          status
        }
        firebaseId
      }
    }
  }
`;

export function useGetUserQuery(variables: { firebaseId: string }) {
  return useQuery(GET_USER, {
    variables,
    skip: variables.firebaseId === '',
    fetchPolicy: 'no-cache',
  });
}
export function useGetUsersQuery(variables: {
  search: string;
  page: number;
  length: number;
}) {
  return useQuery(GET_USERS, {
    variables,
    fetchPolicy: 'no-cache',
  });
}

const CREATE_USER = gql`
  mutation createUser(
    $action: String
    $source: String!
    $firebaseId: String!
    $firstName: String!
    $lastName: String!
    $email: String!
    $phone: String
    $company: String
    $profilePicUrl: String
    $userType: String
    $municipalityId: String
    $status: String
    $userRoleId: String!
    $userTypeId: String!
    $otherRoleData: String
  ) {
    createUser(
      action: $action
      source: $source
      firebaseId: $firebaseId
      firstName: $firstName
      lastName: $lastName
      email: $email
      phone: $phone
      company: $company
      profilePicUrl: $profilePicUrl
      userType: $userType
      municipalityId: $municipalityId
      status: $status
      userRoleId: $userRoleId
      userTypeId: $userTypeId
      otherRoleData: $otherRoleData
    ) {
      ... on User {
        firebaseId
        firstName
        lastName
        email
        phone
        company
        profilePicUrl
        userType
        municipalityId
        status
        emailVerification {
          completed
        }
        source
      }
      ... on UserError {
        message
        success
      }
    }
  }
`;
export function useCreateUserMutation() {
  return useMutation(CREATE_USER);
}

const INVITE_USERS = gql`
  mutation inviteUsers($selectedUsers: String, $teamId: String) {
    inviteUsersToTeam(selectedUsers: $selectedUsers, teamId: $teamId) {
      success
      message
    }
  }
`;
export function useInviteUsers() {
  return useMutation(INVITE_USERS);
}

const GET_USERS_BYKEYWORD = gql`
  mutation getUsersByKeyword($search: String) {
    getUsersByKeyword(search: $search) {
      success
      message
      user {
        firstName
        lastName
        email
        phone
        company
        userType
        emailVerification {
          completed
          code
        }
        firebaseId
      }
    }
  }
`;
export function useGetUsersbyKeywordQuery(variables: { search: string }) {
  return useMutation(GET_USERS_BYKEYWORD, {
    variables,
  });
}

const PROCESS_ACCOUNT = gql`
  mutation processAccount($firebaseId: String!) {
    processAccount(firebaseId: $firebaseId) {
      message
      success
    }
  }
`;

export function useProcessAccountQuery() {
  return useMutation(PROCESS_ACCOUNT);
}

const CREATE_NEW_USER = gql`
  mutation createNewUserAccount(
    $userType: String!
    $firstName: String!
    $lastName: String!
    $userRoleId: String!
    $userTypeId: String!
    $firebaseId: String
    $email: String!
    $phone: String
    $municipalityId: String
    $status: String
    $action: String
    $profilePicUrl: String
    $company: String
    $titleCompany: String
    $titleCompanyId: String
  ) {
    createNewUserAccount(
      userType: $userType
      firstName: $firstName
      lastName: $lastName
      userRoleId: $userRoleId
      userTypeId: $userTypeId
      firebaseId: $firebaseId
      email: $email
      phone: $phone
      municipalityId: $municipalityId
      profilePicUrl: $profilePicUrl
      status: $status
      action: $action
      company: $company
      titleCompany: $titleCompany
      titleCompanyId: $titleCompanyId
    ) {
      message
      success
      user {
        id
        firstName
        lastName
        email
        company
        phone
        profilePicUrl
        userType
        emailVerification {
          completed
          code
        }
        roleData {
          _id
          key
          name
          description
          status
        }
        userTypeData {
          _id
          name
          key
          status
        }
        firebaseId
        municipalityName
        municipalityId
        titleCompanyId
        titleCompany
        status
      }
    }
  }
`;
export function useCreateNewUserQuery() {
  return useMutation(CREATE_NEW_USER);
}

const DELETE_USER = gql`
  mutation deleteUser($firebaseId: String!) {
    deleteUser(firebaseId: $firebaseId) {
      message
      success
      user {
        id
        firstName
        lastName
        email
        company
        phone
        profilePicUrl
        userType
        emailVerification {
          completed
          code
        }
        firebaseId
        municipalityId
        status
        deleted
      }
    }
  }
`;
export function useDeleteUserQuery() {
  return useMutation(DELETE_USER);
}

const GET_ALL_USER_ROLES = gql`
  query getAllUserRoles {
    getAllUserRoles {
      success
      message
      userRoles {
        _id
        name
        description
      }
    }
  }
`;
export function useGetUserRoles() {
  return useQuery(GET_ALL_USER_ROLES, {});
}
const GET_USER_TYPES = gql`
  query getUserTypesWithRoles {
    getUserTypesWithRoles {
      success
      message
      userTypes {
        _id
        name
        key
        userRoles {
          _id
          key
          name
          description
        }
      }
    }
  }
`;
export function useGetUserTypesWithRoles() {
  return useQuery(GET_USER_TYPES, {});
}

const GET_ROLE_PERMISSION = gql`
  mutation getRolePermissions($roleId: String!) {
    getRolePermissions(roleId: $roleId) {
      success
      message
      rolePermissions {
        _id
        name
        description
        status
        key
        permissions {
          _id
          name
          type
          key
          selected
          selectedKey
          selectedValue
          option_type
          option_value
          options
          children
        }
      }
    }
  }
`;

export function useGetRolePermissions() {
  return useMutation(GET_ROLE_PERMISSION);
}

const UPDATE_ROLE_PERMISSIONS = gql`
  mutation updateRolePermissions(
    $roleId: String!
    $name: String!
    $description: String
    $status: String!
    $permissions: [permissionsInput]
  ) {
    updateRolePermissions(
      roleId: $roleId
      name: $name
      description: $description
      status: $status
      permissions: $permissions
    ) {
      success
      message
      rolePermissions {
        _id
        name
        description
        permissions {
          _id
          name
          type
          key
          selected
          selectedValue
          option_type
          options
          children
        }
      }
    }
  }
`;

export function useUpdateRolePermissions() {
  return useMutation(UPDATE_ROLE_PERMISSIONS);
}

const ADD_USER_ROLE = gql`
  mutation addNewRole(
    $name: String!
    $description: String
    $status: String!
    $userTypeId: String
    $userTypeName: String
    $userTypeKey: String
  ) {
    addNewRole(
      name: $name
      description: $description
      status: $status
      userTypeId: $userTypeId
      userTypeName: $userTypeName
      userTypeKey: $userTypeKey
    ) {
      success
      message
      userRole {
        _id
        name
        description
        status
        userTypeId
      }
    }
  }
`;
export function useAddRole() {
  return useMutation(ADD_USER_ROLE);
}

const GET_SINGUP_USERROLES = gql`
  query getUserSignUpRoles {
    getUserSignUpRoles {
      success
      message
      userRoles {
        _id
        name
        key

        status
        userTypeId
      }
    }
  }
`;
export function useGetSignupRoles() {
  return useQuery(GET_SINGUP_USERROLES, {});
}

const ADD_REMOVE_FIREBASE_TOKEN = gql`
  mutation addOrRemoveFireBaseToken(
    $firebaseId: String!
    $token: String!
    $expiryDateTime: String!
    $action: String!
  ) {
    addOrRemoveFireBaseToken(
      firebaseId: $firebaseId
      token: $token
      expiryDateTime: $expiryDateTime
      action: $action
    ) {
      success
      message
    }
  }
`;
export function useAddOrRemoveToken() {
  return useMutation(ADD_REMOVE_FIREBASE_TOKEN);
}
